import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { MapPinOff } from 'lucide-react';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { SpawnRow } from '../../components/world/SpawnRow';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { MonsterItemDrops } from '../../components/world/MonsterItemDrops';
import { EventPositionList } from '../../components/world/EventPositionList';
import { NpcCard } from '../../components/progression/NpcCard';
import { npcs } from '../../data/guide/npcs';
import { monsterKind } from '../../utils/monsterKind';
import { useEventPositions } from '../../hooks/useEventPositions';
import { positionsFor } from '../../utils/eventPositions';
import { EntityImage } from '../../components/world/EntityImage';
import { imageEntryForMonster } from '../../utils/entityImages';

/** Posiciones del monstruo dentro de eventos (32_event_spawn_rules), separadas de los spots permanentes. */
function MonsterEventPositions({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const ep = useEventPositions();
  const list = ep ? positionsFor(ep.positions, monsterId) : [];
  if (!list.length) return null;
  return <WorldSection id="posiciones-evento" title="Posiciones dentro de eventos" count={list.length} data-id="element-2888" data-mp="1geziqw0ji03p">
      <EventPositionList w={w} positions={list} max={12} data-id="element-2889" data-mp="1le2mmd4sa0a3" />
    </WorldSection>;
}
import { BoxContents } from '../../components/world/BagSourceList';
import { monsterBags } from '../../utils/bagSources';
import { mapName, mapPath, formatSeconds, monsterPath } from '../../utils/worldFormat';
import { CanonicalPath } from '../../components/world/CanonicalPath';
import { NpcEntityView } from '../../components/world/NpcEntityView';
import { monsterIdFromPrettyPath } from '../../utils/prettyRoutes';
import { canonicalNpcId } from '../../data/guide/npcIdentity';
import type { Monster, WorldData } from '../../types/world';
export function MonsterDetail() {
  const {
    monsterId,
    slug
  } = useParams();
  const {
    pathname
  } = useLocation();
  useHashScroll();
  return <WorldGate data-id="element-2890" data-mp="15q3cowht1jlc">
      {w => {
      const rawId = slug ? monsterIdFromPrettyPath(pathname) : Number(monsterId);
      // Enlaces antiguos de un NPC unificado (Pet Trainer 234) abren la ficha principal (Trainer 226).
      const id = rawId === undefined ? undefined : canonicalNpcId(rawId);
      const m = id === undefined ? undefined : w.monsters.get(id);
      if (!m) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2891" data-mp="mzvemssypjc0">
              <p className="text-white" data-id="element-2892" data-mp="3vfxbxamjek9j">No encontramos ese monstruo en los archivos.</p>
              <Link to="/guia/mapas?vista=monstruos" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2893" data-mp="3qhngcywwhhne">Volver a Monstruos</Link>
            </div>;
      }
      return <>
            <CanonicalPath to={monsterPath(m.id)} data-id="element-2894" data-mp="2asntwemi6rzw" />
            {monsterKind(w, m) === 'npc' ? <NpcEntityView w={w} m={m} data-id="element-2895" data-mp="1x0r4iic7yw77" /> : <MonsterView w={w} m={m} data-id="element-2896" data-mp="1x1j19whjyzz2" />}
          </>;
    }}
    </WorldGate>;
}

/** Procedencia de la foto, separada de la imagen y de los datos de juego. */
function PhotoProvenance({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const img = imageEntryForMonster(w, monsterId)?.image;
  if (!img || img.origin !== 'paquete') return null;
  const family = img.familyReference?.sharedWith ?? [];
  return <Disclosure title="Procedencia de la imagen" data-id="element-2897" data-mp="2kpadl033fd0c">
      <p className="text-sm text-gray-300" data-id="element-2898" data-mp="2knza71dk879r">
        Foto de referencia visual{img.packName ? ` (paquete ${img.packName})` : ''}{img.sourceName ? `, rotulada «${img.sourceName}»` : ''}. Retocada para quitar la marca: puede tener
        pequeñas diferencias de textura. No es una captura del cliente Vanguard ni confirma drops, estadísticas ni ubicaciones.
      </p>
      {family.length > 0 && <p className="mt-2 text-sm text-gray-400" data-id="element-2899" data-mp="1js8riumecml5">
          Referencia de familia: la misma foto se usa para los ID {family.join(', ')}. Cada ID conserva su propia ficha, mapas, drops y estadísticas.
        </p>}
      <p className="mt-2 text-xs text-gray-500" data-id="element-2900" data-mp="1au6vkthisk4q">{img.matchedBy}</p>
    </Disclosure>;
}
function MonsterView({
  w,
  m
}: {
  w: WorldData;
  m: Monster;
}) {
  useEffect(() => {
    document.title = `${m.name} · Monstruos · Guía MU Vanguard`;
  }, [m.name]);
  const locations = w.idx.locationsByMonster.get(m.id) ?? [];
  // Solo NPC con identidad confirmada (monsterId); los candidatos de Pet Trainer no reciben su tarjeta.
  const guideNpc = npcs.find(n => n.monsterId === m.id);
  const permanent = locations.filter(l => l.kind === 'ordinary');
  const temporary = locations.filter(l => l.kind !== 'ordinary');
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? [];
  const spots = spawns.filter(s => s.kind === 'spot');
  const invasionSpawns = spawns.filter(s => s.kind === 'invasion');
  const eventIds = Array.from(new Set([...locations, ...spawns].map(x => x.eventId).filter((v): v is string => !!v)));
  return <div data-id="element-2901" data-mp="hw2zgkq89dly">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: 'Monstruos',
      to: '/guia/mapas?vista=monstruos'
    }, {
      label: m.name
    }]} eyebrow={guideNpc || monsterKind(w, m) === 'npc' ? 'NPC' : 'Monstruo'} title={m.name} media={imageEntryForMonster(w, m.id)?.image ? <EntityImage entry={imageEntryForMonster(w, m.id)} alt={m.name} name={m.name} size="hero" lazy={false} kind={guideNpc ? 'persona' : 'monstruo'} data-id="element-2903" data-mp="10pqfbz04p10e" /> : undefined} data-id="element-2902" data-mp="monufpkvdwr2">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2904" data-mp="32gk1540pmegu">
          {m.level !== undefined && <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-2905" data-mp="37fwmfkbxndhb">Nivel del monstruo (catálogo): {m.level}</span>}
          <CopyLinkButton label="Copiar enlace" data-id="element-2906" data-mp="2ufvxl04iv6k7" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-2907" data-mp="39cwe07h9jf3d">
        <WorldSection id="ubicaciones" title="Dónde aparece" data-id="element-2908" data-mp="39d4ue9clh2jb">
          {guideNpc ?
        // NPC de las guías: misma resolución de ubicación que en misiones, recetas y buscador.
        <NpcCard npcId={guideNpc.id} data-id="element-2909" data-mp="3ibzztj7te8lk" /> : locations.length === 0 ? <p className="flex items-start gap-2 rounded-2xl border border-white/10 p-4 text-gray-300" data-id="element-2910" data-mp="3deqa02gijux7">
              <MapPinOff className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-2911" data-mp="3s1t8abcnu7ll" />
              Ubicación no documentada en los archivos disponibles.
            </p> : <div className="grid gap-6 md:grid-cols-2" data-id="element-2912" data-mp="4xdfy4rwumi2">
              {[{
            title: 'Presencia permanente',
            list: permanent
          }, {
            title: 'Solo durante eventos o invasiones',
            list: temporary
          }].map(g => g.list.length > 0 && <div key={g.title} data-id="element-2913" data-mp="ojyf5tntu4y7">
                      <h3 className="mb-2 text-sm font-semibold text-gray-400" data-id="element-2914" data-mp="oivrrvppqkn1">{g.title}</h3>
                      <ul className="space-y-2" data-id="element-2915" data-mp="akyuex3fqa7b">
                        {g.list.map(l => <li key={l.id} className="flex flex-wrap items-center gap-2" data-id="element-2916" data-mp="fih26k5pujdq">
                            <Link to={mapPath(l.mapId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-2917" data-mp="tubxnejw77q">
                              {mapName(w, l.mapId)}
                            </Link>
                            {l.eventId && <EventChip w={w} eventId={l.eventId} data-id="element-2918" data-mp="3nx8wbydkzlxr" />}
                          </li>)}
                      </ul>
                    </div>)}
            </div>}
        </WorldSection>

        <WorldSection id="spots" title="Spots y áreas" count={spots.length} data-id="element-2919" data-mp="3iynw3yybx29b">
          {spots.length ? <ul className="grid gap-2.5" data-id="element-2920" data-mp="2c2r58u8eypb">{spots.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2921" data-mp="3tj5gduqieuww" />)}</ul> : <p className="text-gray-400" data-id="element-2922" data-mp="3th2thjknlhwe">No hay spots permanentes configurados para este monstruo.</p>}
        </WorldSection>

        <MonsterEventPositions w={w} monsterId={m.id} data-id="element-2923" data-mp="3kj0l2iylbmz3" />

        {invasionSpawns.length > 0 && <WorldSection id="invasiones" title="Apariciones en invasiones" count={invasionSpawns.length} data-id="element-2924" data-mp="3fje7u9iri6qa">
            <p className="mb-3 text-sm text-gray-400" data-id="element-2925" data-mp="3as5vb4m4y58w">Cada fila es un registro. Las filas de mapas alternativos no son monstruos adicionales.</p>
            <ul className="grid gap-2.5" data-id="element-2926" data-mp="35r8ht5c2lzdq">{invasionSpawns.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2927" data-mp="31sixstwj1jl4" />)}</ul>
          </WorldSection>}

        {eventIds.length > 0 && <WorldSection id="eventos" title="Eventos e invasiones relacionados" data-id="element-2928" data-mp="2wrv4u2oqmso9">
            <div className="flex flex-wrap gap-2" data-id="element-2929" data-mp="2ruuydqnz7nwm">{eventIds.map(id => <EventChip key={id} w={w} eventId={id} data-id="element-2930" data-mp="17brg9j9rkcnc" />)}</div>
          </WorldSection>}

        <MonsterItemDrops w={w} monsterId={m.id} data-id="element-2931" data-mp="1cac7it71flzn" />

        <BoxContents w={w} bags={monsterBags(w, m.id)} id="recompensas" title="Recompensas posibles al derrotarlo" intro="Lista interna de resultados posibles al derrotar a este monstruo. No es una caja que abras ni se entregan todos juntos." data-id="element-2932" data-mp="1c915axt8qman" />

        <Disclosure title="Estadísticas de catálogo" data-id="element-2933" data-mp="omunjju0ls78">
          <StatList rows={[{
          label: 'Nivel (catálogo)',
          value: m.level
        }, {
          label: 'Vida (catálogo)',
          value: m.hp
        }, {
          label: 'Daño mínimo (catálogo)',
          value: m.damageMin
        }, {
          label: 'Daño máximo (catálogo)',
          value: m.damageMax
        }, {
          label: 'Defensa (catálogo)',
          value: m.defense
        }, {
          label: 'Reaparición (catálogo)',
          value: m.respawnSeconds !== undefined ? formatSeconds(m.respawnSeconds) : undefined
        }, {
          label: 'item_rate_divisor',
          value: m.itemRateDivisor,
          note: 'no es una probabilidad de drop'
        }, {
          label: 'ID del monstruo',
          value: m.id
        }]} data-id="element-2934" data-mp="tjl3861wqls4" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-2935" data-mp="1ijcgpn8w720n">Valores generales del catálogo. Cada spot puede tener estadísticas configuradas distintas; míralas en la ficha del spot.</p>
        </Disclosure>

        <PhotoProvenance w={w} monsterId={m.id} data-id="element-2936" data-mp="qt7xvt0s6n1j" />
      </div>
    </div>;
}